// ===============================================
// File: src/components/Planner/PlannerViews/ListView.jsx
// Stage normalization + persisted filters + "Show done"
// NEW:
//  - Title wraps (textarea with auto-resize)
//  - Row double-click (not on form controls) opens quick editor
//  - View button per row
//  - Responsive grid columns (fit container on web + iPad)
// ===============================================

import React, { useEffect, useMemo, useRef, useState } from "react";
import { usePlanner } from "../../../contexts/PlannerContext";
import { useStaff } from "../../../contexts/StaffContext";
import TaskEditorModal from "./TaskEditorModal";

/* ---------- stage maps ---------- */
const STAGE_LABELS = ["Forming","Drying","Bisque","Glazing","Glaze Firing","QC & Pack","Dispatch"];
const LABEL_TO_CANON = {
  "Forming":"forming","Drying":"drying","Bisque":"bisque","Glazing":"glazing",
  "Glaze Firing":"glazeFiring","QC & Pack":"qcPack","Dispatch":"dispatch",
};
const CANON_TO_LABEL = {
  forming:"Forming",drying:"Drying",bisque:"Bisque",glazing:"Glazing",
  glazeFiring:"Glaze Firing",qcPack:"QC & Pack",dispatch:"Dispatch",
};
const STAGE_KEYS = new Set(Object.keys(CANON_TO_LABEL));

/* ---------- helpers ---------- */
const fmtTime = (d) => { try { return new Date(d).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
const fmtDate = (d) => { try { return new Date(d).toISOString().slice(0, 10); } catch { return ""; } };
const compactId = (v) => { const s = String(v || ""); return !s ? "—" : s.length > 10 ? `${s.slice(0,8)}…` : s; };
const newId = () => (globalThis.crypto?.randomUUID?.() ?? `pln_${Date.now().toString(36)}_${Math.random().toString(36).slice(2,8)}`);

const LS_CANDIDATES = ["mudoPlannerTasks","mudoPlannerEvents","plannerEvents"];
const LS_UI = "mudoListViewPrefs";
function lsRead() {
  for (const k of LS_CANDIDATES) {
    try {
      const raw = localStorage.getItem(k);
      const arr = raw ? JSON.parse(raw) : null;
      if (Array.isArray(arr)) return { key:k, events:arr };
      if (arr && Array.isArray(arr?.events)) return { key:k, events:arr.events };
    } catch {}
  }
  return null;
}
function lsWrite(key, events) {
  try {
    const raw = localStorage.getItem(key);
    const wasObj = raw && /^\s*\{/.test(raw);
    localStorage.setItem(key, JSON.stringify(wasObj ? { events } : events));
    window.dispatchEvent(new Event("mudo:planner-updated"));
  } catch {}
}

/* ---------- stage util (keeps tag in sync) ---------- */
function stageLabelFromEvent(ev) {
  const tag = (Array.isArray(ev?.tags) ? ev.tags : []).find(t => STAGE_KEYS.has(t));
  if (tag && CANON_TO_LABEL[tag]) return CANON_TO_LABEL[tag];
  const st = String(ev?.stage || "").trim();
  if (!st) return "";
  if (CANON_TO_LABEL[st]) return CANON_TO_LABEL[st];
  if (LABEL_TO_CANON[st]) return st;
  if (st.replace(/\s+/g,"")==="GlazeFiring") return "Glaze Firing";
  const lc = st.charAt(0).toLowerCase()+st.slice(1);
  if (CANON_TO_LABEL[lc]) return CANON_TO_LABEL[lc];
  return "";
}
function applyStagePatch(ev, label) {
  const canon = LABEL_TO_CANON[label] || undefined;
  const baseTags = Array.isArray(ev?.tags) ? ev.tags : [];
  const nonStage = baseTags.filter(t => !STAGE_KEYS.has(t));
  const tags = canon ? [...nonStage, canon] : nonStage;
  return { stage: label || undefined, tags: tags.length ? tags : undefined };
}

/* ---------- shared responsive grid template ---------- */
// Uses fractional units instead of large fixed px widths,
// so the list view fits nicely within the planner container
// on desktop and iPad.
const GRID_COLS =
  "minmax(180px,2fr)  minmax(160px,1.4fr)  minmax(60px,0.7fr)  minmax(100px,1fr)  minmax(130px,1.1fr)  minmax(170px,1.4fr)  minmax(200px,1.6fr)  minmax(150px,1.1fr)";

/* ---------- Component ---------- */
export default function ListView({
  events: eventsOverride,
  openEventId = null,
  onDeepLinkHandled,
}) {
  const planner = usePlanner();
  const { staffList = [] } = useStaff();
  const events = Array.isArray(eventsOverride) ? eventsOverride : (planner?.events || []);

  // persisted UI prefs
  const persisted = (() => { try { return JSON.parse(localStorage.getItem(LS_UI) || "{}"); } catch { return {}; } })();
  const [q, setQ] = useState(persisted.q || "");
  const [assigneeFilter, setAssigneeFilter] = useState(persisted.assigneeFilter || "all");
  const [showDone, setShowDone] = useState(persisted.showDone ?? false);

  const [editing, setEditing] = useState(null);
  const [hiddenIds, setHiddenIds] = useState(() => new Set());

  useEffect(() => {
    if (!openEventId) return;
    const match = (events || []).find((ev) => String(ev.id) === String(openEventId));
    if (match) {
      setEditing(match);
      onDeepLinkHandled?.(true);
    } else if (events?.length >= 0) {
      onDeepLinkHandled?.(false);
    }
  }, [openEventId, events, onDeepLinkHandled]);

  useEffect(() => { try { localStorage.setItem(LS_UI, JSON.stringify({ q, assigneeFilter, showDone })); } catch {} }, [q,assigneeFilter,showDone]);

  /* quick-add row state */
  const now = new Date();
  const defaultStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 9, 0, 0, 0);
  const [quick, setQuick] = useState({
    title:"", date:fmtDate(defaultStart), time:"09:00", durationMins:60, qty:1,
    batch:"", stage:"", category:"", assigneeId:"unassigned", notes:""
  });

  const makeDate = (dateStr, timeStr) => {
    try { const [h,m]=(timeStr||"09:00").split(":").map((n)=>parseInt(n,10)); const d=new Date(dateStr); d.setHours(h||0,m||0,0,0); return d; }
    catch { return new Date(); }
  };

  /* ---------- core mutators with robust fallbacks ---------- */
  const applyPatchById = (id, patch) => {
    if (!id) return;
    if (typeof planner.updateEventPatch === "function") { planner.updateEventPatch(id, patch); return; }
    if (typeof planner.upsertEvents === "function") {
      const cur = (events || []).find((e) => String(e.id) === String(id)) || { id };
      planner.upsertEvents([{ ...cur, ...patch }]);
      return;
    }
    if (typeof planner.setEvents === "function") {
      planner.setEvents((prev = []) => prev.map((e) => (String(e.id) === String(id) ? { ...e, ...patch } : e)));
      return;
    }
    const ls = lsRead();
    if (ls) {
      const next = (ls.events || []).map((e) => String(e.id) === String(id) ? { ...e, ...patch } : e);
      lsWrite(ls.key, next);
    }
  };
  const doMarkDone = (ev) => { if (!ev?.id) return; if (typeof planner.markDone === "function") return planner.markDone(ev.id); applyPatchById(ev.id, { status:"done", progress:100 }); };
  const doUnassign = (ev) => { if (!ev?.id) return; applyPatchById(ev.id, { assigneeId:null, assignee:null, assigneeName:null }); };
  const patch = (ev, changes) => { if (!ev?.id) return; applyPatchById(ev.id, changes); };

  const addQuick = () => {
    if (!quick.title.trim()) return;
    const start = makeDate(quick.date, quick.time);
    const end = new Date(start.getTime() + Number(quick.durationMins || 60) * 60000);
    const stagePatch = quick.stage ? applyStagePatch({}, quick.stage) : { stage: undefined, tags: undefined };
    const base = {
      id:newId(), title:quick.title.trim(), start:start.toISOString(), end:end.toISOString(),
      qty:Number(quick.qty || 1), batch:quick.batch || undefined, category:quick.category || undefined,
      status:"todo", progress:0, notes:(quick.notes || "").trim(), ...stagePatch,
    };
    const asg = quick.assigneeId === "unassigned"
      ? { assigneeId:null, assignee:null, assigneeName:null }
      : { assigneeId:quick.assigneeId, assignee:undefined, assigneeName:undefined };

    if (typeof planner.upsertEvents === "function") planner.upsertEvents([{ ...base, ...asg }]);
    else if (typeof planner.setEvents === "function") planner.setEvents((prev=[]) => [{ ...base, ...asg }, ...prev]);
    else { const ls = lsRead() || { key: LS_CANDIDATES[0], events: [] }; lsWrite(ls.key, [{ ...base, ...asg }, ...(ls.events || [])]); }

    setQuick((q)=>({ ...q, title:"", qty:1, batch:"", notes:"" }));
  };

  /* categories for datalist */
  const categories = useMemo(() => {
    const set = new Set(); (events || []).forEach((e) => { if (e?.category) set.add(e.category); });
    return Array.from(set).sort();
  }, [events]);

  const staffOptions = useMemo(() => {
    const opts = [{ id:"all", label:"All Staff" }];
    (staffList || []).forEach((s) => opts.push({ id:String(s.id), label:s.name || compactId(s.id) }));
    return opts;
  }, [staffList]);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return (events || [])
      .filter((e) => !hiddenIds.has(String(e.id)))
      .filter((e) => (showDone ? true : (e?.status !== "done" && (e?.progress ?? 0) < 100)))
      .filter((e) => {
        const hit = !qq ||
          String(e?.title || "").toLowerCase().includes(qq) ||
          String(e?.category || "").toLowerCase().includes(qq) ||
          String(e?.batch || "").toLowerCase().includes(qq) ||
          String(e?.notes || "").toLowerCase().includes(qq);
        const asgOk = assigneeFilter === "all" || String(e?.assigneeId || e?.assignee || "") === assigneeFilter;
        return hit && asgOk;
      });
  }, [events,q,assigneeFilter,hiddenIds,showDone]);

  const groups = useMemo(() => {
    const forming = [], other = [];
    filtered.forEach((e) => (stageLabelFromEvent(e) === "Forming" ? forming : other).push(e));
    const byStart = (a,b) => new Date(a?.start || 0) - new Date(b?.start || 0);
    forming.sort(byStart); other.sort(byStart);
    return [{ key:"FORMING", items:forming }, { key:"OTHER", items:other }];
  }, [filtered]);

  // optimistic delete
  async function handleDelete(ev) {
    const id = String(ev?.id || ""); if (!id) return;
    const ok = window.confirm(`Delete “${ev.title || "Untitled"}”?`); if (!ok) return;
    setHiddenIds((prev) => new Set(prev).add(id));
    try {
      if (typeof planner.deleteEvent === "function") { await planner.deleteEvent(id); return; }
      if (typeof planner.deleteEvents === "function") { await planner.deleteEvents([id]); return; }
      if (typeof planner.setEvents === "function") { planner.setEvents((prev=[]) => prev.filter((x)=>String(x.id)!==id)); return; }
      const ls = lsRead(); if (ls) lsWrite(ls.key, (ls.events || []).filter((x)=>String(x.id)!==id));
    } catch (e) {
      console.error("[Planner ListView] delete failed:", e);
      setHiddenIds((prev) => { const n = new Set(prev); n.delete(id); return n; });
    }
  }

  const textColor = "var(--mudo-text, #fff)";
  const bgColor = "rgba(255,255,255,.06)";
  const bgLight = "rgba(0,0,0,.06)";
  const borderColor = "rgba(255,255,255,.12)";

  const isFormControl = (el) => {
    const tag = (el?.tagName || "").toLowerCase();
    return ["input","select","textarea","button"].includes(tag) || el?.closest?.("input,select,textarea,button");
  };

  // auto-resize helper for wrapping title textarea
  const autoResize = (el) => {
    if (!el) return;
    el.style.height = "0px";
    el.style.height = Math.min(el.scrollHeight, 200) + "px";
  };

  return (
    <div className="space-y-3 mudo-list">
      <style>{`
        html:not(.dark) .mudo-list .cell-input,
        html:not(.dark) .mudo-list .cell-select,
        html:not(.dark) .mudo-list .cell-textarea {
          background: ${bgLight} !important;
          color: #0b1220 !important;
          border: 1px solid rgba(11,18,32,.18) !important;
        }
        html:not(.dark) .mudo-list .toolbar-select option { color:#0b1220; background:#fff; }
      `}</style>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={assigneeFilter}
          onChange={(e) => setAssigneeFilter(e.target.value)}
          className="px-3 py-1 rounded text-sm toolbar-select"
          style={{ background:bgColor, color:textColor, border:`1px solid ${borderColor}` }}
        >
          {(() => [{ id:"all", label:"All Staff" }, ...staffOptions.slice(1)] )().map((o) => (
            <option key={o.id} value={o.id} style={{ background:"#222", color:"#fff" }}>{o.label}</option>
          ))}
        </select>

        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search…"
          className="px-3 py-1 rounded text-sm w-56"
          style={{ background:bgColor, color:textColor, border:`1px solid ${borderColor}` }}
          onKeyDown={(e) => { if (e.key === "Escape") setQ(""); }}
        />

        <label className="text-sm flex items-center gap-2 ml-auto opacity-80">
          <input type="checkbox" checked={showDone} onChange={(e)=>setShowDone(e.target.checked)} />
          Show done
        </label>
      </div>

      {/* Quick add row */}
      <div
        className="grid gap-2 px-3 py-3 rounded-xl"
        style={{
          gridTemplateColumns: GRID_COLS,
          border:`1px dashed ${borderColor}`, background:"rgba(255,255,255,.03)",
        }}
      >
        <input
          className="cell-input px-2 py-1 rounded w-full"
          placeholder="New task…"
          value={quick.title}
          onChange={(e) => setQuick({ ...quick, title: e.target.value })}
          onKeyDown={(e)=>{ if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); addQuick(); } }}
        />
        <div className="flex gap-2">
          <input type="date" className="cell-input px-2 py-1 rounded w-[140px]" value={quick.date} onChange={(e)=>setQuick({ ...quick, date:e.target.value })}/>
          <input type="time" className="cell-input px-2 py-1 rounded w-[110px]" value={quick.time} onChange={(e)=>setQuick({ ...quick, time:e.target.value })}/>
        </div>
        <input type="number" min="0" className="cell-input px-2 py-1 rounded w-full" value={quick.qty} onChange={(e)=>setQuick({ ...quick, qty:Number(e.target.value) })} title="Quantity"/>
        <input className="cell-input px-2 py-1 rounded w-full" placeholder="Batch" value={quick.batch} onChange={(e)=>setQuick({ ...quick, batch:e.target.value })}/>
        <select className="cell-select px-2 py-1 rounded w-full" value={quick.stage} onChange={(e)=>setQuick({ ...quick, stage:e.target.value })}>
          <option value="">—</option>{STAGE_LABELS.map((s)=><option key={s} value={s}>{s}</option>)}
        </select>
        <div className="flex gap-2">
          <input list="mudo-catlist" className="cell-input px-2 py-1 rounded w-full" placeholder="Category" value={quick.category} onChange={(e)=>setQuick({ ...quick, category:e.target.value })}/>
          <select className="cell-select px-2 py-1 rounded" value={quick.assigneeId} onChange={(e)=>setQuick({ ...quick, assigneeId:e.target.value })} title="Assignee">
            <option value="unassigned">Unassigned</option>
            {staffList.map((s)=><option key={s.id} value={String(s.id)}>{s.name || compactId(s.id)}</option>)}
          </select>
        </div>
        <input className="cell-input px-2 py-1 rounded w-full" placeholder="Notes (optional)" value={quick.notes} onChange={(e)=>setQuick({ ...quick, notes:e.target.value })} onKeyDown={(e)=>{ if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); addQuick(); } }}/>
        <div className="flex items-center justify-end">
          <button type="button" onClick={addQuick} className="px-3 py-1 rounded text-sm bg-emerald-600 text-white">Add</button>
        </div>
      </div>

      {/* Groups */}
      {groups.map((g) => (
        <div key={g.key} className="space-y-2">
          <div className="text-xs uppercase tracking-wider opacity-70">{g.key}</div>

          <div className="rounded-xl" style={{ border:`1px solid ${borderColor}`, overflow:"hidden" }}>
            <div
              className="grid gap-2 px-3 py-2 text-[12px]"
              style={{
                gridTemplateColumns: GRID_COLS,
                background:"rgba(255,255,255,.04)",
              }}
            >
              <div>Task</div>
              <div>When</div>
              <div>Qty</div>
              <div>Batch</div>
              <div>Stage</div>
              <div>Category / Assignee</div>
              <div>Notes</div>
              <div></div>
            </div>

            {g.items.length === 0 ? (
              <div className="px-3 py-4 text-sm opacity-60">No items.</div>
            ) : (
              g.items.map((ev) => {
                const when = `${fmtDate(ev.start)} ${fmtTime(ev.start)} — ${fmtDate(ev.end)} ${fmtTime(ev.end)}`.trim();
                const qty = ev?.qty ?? 1;
                const batch = ev?.batch || "";
                const assigneeValue = ev?.assigneeId ? String(ev.assigneeId) : "unassigned";
                const notesVal = (ev?.notes ?? "").toString();
                const stageLabel = stageLabelFromEvent(ev);

                return (
                  <div
                    key={ev.id}
                    className="grid items-center gap-2 px-3 py-2 text-[13px] border-t"
                    style={{
                      gridTemplateColumns: GRID_COLS,
                      borderColor:"rgba(255,255,255,.08)",
                    }}
                    onDoubleClick={(e) => { if (!isFormControl(e.target)) setEditing(ev); }}
                    title="Tip: Double-click row to quick view"
                  >
                    {/* Title: wrapping textarea with auto-resize */}
                    <textarea
                      rows={2}
                      className="cell-textarea px-2 py-1 rounded w-full resize-y leading-snug whitespace-pre-wrap break-words"
                      defaultValue={ev.title || "(untitled)"}
                      onFocus={(e) => autoResize(e.target)}
                      onInput={(e) => autoResize(e.target)}
                      onBlur={(e) => {
                        const v = e.target.value;
                        if (v !== (ev.title || "")) patch(ev, { title: v });
                        autoResize(e.target);
                      }}
                    />

                    <div className="truncate opacity-90">{when}</div>

                    <input
                      type="number"
                      min="0"
                      className="cell-input px-2 py-1 rounded w-full"
                      defaultValue={qty}
                      onBlur={(e) => {
                        const v = Number(e.target.value || 0);
                        if (v !== (ev.qty ?? 1)) patch(ev, { qty: v });
                      }}
                    />

                    <input
                      className="cell-input px-2 py-1 rounded w-full"
                      defaultValue={batch}
                      placeholder="—"
                      onBlur={(e) => {
                        const v = e.target.value || "";
                        if (v !== (ev.batch || "")) patch(ev, { batch: v || undefined });
                      }}
                    />

                    {/* Stage */}
                    <select
                      className="cell-select px-2 py-1 rounded w-full"
                      value={stageLabel}
                      onChange={(e) => {
                        const label = e.target.value || "";
                        const stagePatch = label ? applyStagePatch(ev, label) : { stage: undefined, tags: (ev.tags || []).filter(t=>!STAGE_KEYS.has(t)) };
                        patch(ev, stagePatch);
                      }}
                    >
                      <option value="">—</option>
                      {STAGE_LABELS.map((s)=><option key={s} value={s}>{s}</option>)}
                    </select>

                    <div className="flex flex-wrap items-center gap-2">
                      <div className="min-w-[120px]">
                        <input
                          list="mudo-catlist"
                          className="cell-input px-2 py-1 rounded w-full"
                          defaultValue={ev.category || ""}
                          placeholder="Category"
                          onBlur={(e) => {
                            const v = e.target.value || "";
                            if (v !== (ev.category || "")) patch(ev, { category: v || undefined });
                          }}
                        />
                      </div>

                      <select
                        className="cell-select px-2 py-1 rounded"
                        value={assigneeValue}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === "unassigned") doUnassign(ev);
                          else patch(ev, { assigneeId: val, assignee: undefined, assigneeName: undefined });
                        }}
                      >
                        <option value="unassigned">Unassigned</option>
                        {(staffList || []).map((s) => (
                          <option key={s.id} value={String(s.id)}>
                            {s.name || compactId(s.id)}
                          </option>
                        ))}
                      </select>
                    </div>

                    <textarea
                      rows={2}
                      className="cell-textarea px-2 py-1 rounded w-full resize-y"
                      defaultValue={notesVal}
                      placeholder="Notes…"
                      onInput={(e) => autoResize(e.target)}
                      onBlur={(e) => {
                        const v = (e.target.value || "").trim();
                        if (v !== notesVal) patch(ev, { notes: v });
                      }}
                    />

                    <div className="flex items-center gap-2 justify-end">
                      <button
                        type="button"
                        onClick={() => setEditing(ev)}
                        className="px-2 py-1 rounded text-xs"
                        style={{ background:"rgba(59,130,246,.14)", color:"#60a5fa", border:"1px solid rgba(59,130,246,.35)" }}
                        title="View / Edit"
                      >
                        View
                      </button>
                      <button
                        type="button"
                        onClick={() => doMarkDone(ev)}
                        className="px-2 py-1 rounded text-xs"
                        style={{ background:"rgba(34,197,94,.12)", color:"#22c55e", border:"1px solid rgba(34,197,94,.3)" }}
                      >
                        Done
                      </button>
                      <button
                        type="button"
                        onClick={() => doUnassign(ev)}
                        className="px-2 py-1 rounded text-xs"
                        style={{ background:"rgba(234,179,8,.12)", color:"#eab308", border:"1px solid rgba(234,179,8,.3)" }}
                      >
                        Unassign
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(ev)}
                        className="px-2 py-1 rounded text-xs"
                        style={{ background:"rgba(239,68,68,.14)", color:"#ef4444", border:"1px solid rgba(239,68,68,.35)" }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      ))}

      <datalist id="mudo-catlist">
        {categories.map((c) => <option key={c} value={c} />)}
      </datalist>

      {/* Quick editor modal */}
      <TaskEditorModal open={!!editing} event={editing || undefined} onClose={() => setEditing(null)} />
    </div>
  );
}
